/**
 * Day Upgrades Component
 * Displays upgrade cards and manages upgrade modals
 * WHY: Handles modal state locally (contextual to specific days)
 */

import React, { useState } from "react";
import { Stack, Dialog } from "@mui/material";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import type { DayUpgradesProps } from "../types";
import UpgradeCard from "./upgrade-card";
import { ExcursionModal, HotelModal } from "./upgrade-modals";

const DayUpgrades: React.FC<DayUpgradesProps> = ({
  upgrades,
  dayNumber,
  testIdPrefix
}) => {
  const [openModalIndex, setOpenModalIndex] = useState<number | null>(null);

  useScrollLock(openModalIndex !== null);

  const handleOpenModal = (index: number) => {
    setOpenModalIndex(index);
  };

  const handleCloseModal = () => {
    setOpenModalIndex(null);
  };

  if (!upgrades) {
    return null;
  }

  const activeUpgrade = openModalIndex !== null ? upgrades : null;

  let type = "";
  if(upgrades.choiceOnTour && upgrades.choiceOnTour.length > 0) {
    type = "choice";
  } else if(upgrades.roomUpgrades && upgrades.roomUpgrades.length > 0) {  
    type = "roomUpgrade";
  }else if(upgrades.excursion && upgrades.excursion.length > 0) {
    type = "excursion";
  }

  return (
    <>
    
      {/* Upgrade Cards */}
      <Stack
        gap="1.2rem"
        data-testid={`${testIdPrefix}_day-upgrades_${type}`}
        // WHY: Parent Stack gap handles spacing, each upgrade card is full-width row
      >
      
          <UpgradeCard
            key={`upgrade-${dayNumber}`}
            upgrade={upgrades}
            onClick={() => handleOpenModal(0)}
            testIdPrefix={`${testIdPrefix}_day-${type}`}
          />
      </Stack>

      {/* Modals */}
      {activeUpgrade && (
        <>
          {/* Excursion Modal */}
          {type === "excursion" && upgrades.excursion && (
            <Dialog
              open
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
              disableScrollLock
              data-testid={`${testIdPrefix}_excursion-modal`}
            >
              <ExcursionModal
                excursion={upgrades.excursion[0]}
                handleClose={handleCloseModal}
              />
            </Dialog>
          )}

          {/* Hotel Modal */}
          {type === "roomUpgrade" && upgrades.roomUpgrades && (
            <Dialog
              open
              onClose={handleCloseModal}
              maxWidth="md"
              fullWidth
              disableScrollLock
              data-testid={`${testIdPrefix}_hotel-modal`}
            >
              <HotelModal
                room={upgrades.roomUpgrades[0]}
                handleClose={handleCloseModal}
              />
            </Dialog>
          )}
        </>
      )}
    </>
  );
};

export default DayUpgrades;
